/**
 * Access Control Utilities for Weetrak System
 * 
 * This utility provides centralized access control functions based on the role system:
 * 
 * System Roles:
 * - company_admin: Full system access + automatically gets PAYROLL_ADMIN role
 * - branch_user: Limited to their assigned branch, NO payroll access
 * 
 * Payroll Roles (Main Branch only):
 * - PAYROLL_ADMIN: Full payroll control
 * - HR_MANAGER: HR-focused payroll access
 * - ACCOUNTANT: Financial oversight access
 * - PAYROLL_VIEWER: Read-only payroll access
 */

/**
 * Check if user can access payroll features
 * @param {Object} user - User object with role and branch info
 * @returns {boolean} - True if user can access payroll
 */
export const canAccessPayroll = (user) => {
  if (!user) return false;
  
  // Only Main Branch users can access payroll
  const isMainBranch = user.branch_name === 'Main Branch' || 
                       user.branch_name === 'main_branch' ||
                       user.branch_id === 'main' ||
                       user.branch_id === 1; // Assuming main branch has ID 1
  
  return isMainBranch;
};

/**
 * Check if user has a specific payroll role
 * @param {Object} user - User object with payroll_roles array
 * @param {string} role - Role to check for
 * @returns {boolean} - True if user has the role
 */
export const hasPayrollRole = (user, role) => {
  if (!user || !user.payroll_roles) return false;
  return user.payroll_roles.includes(role);
};

/**
 * Get customer role from customer_roles endpoint
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {string|null} - Customer role or null
 */
export const getCustomerRole = (customerRoles) => {
  return customerRoles?.customer_role || null;
};

/**
 * Check if user has a specific payroll permission
 * @param {Object} user - User object with payroll_permissions array
 * @param {string} permission - Permission to check for
 * @returns {boolean} - True if user has the permission
 */
export const hasPayrollPermission = (user, permission) => {
  if (!user || !user.payroll_permissions) return false;
  return user.payroll_permissions.includes(permission);
};

/**
 * Get the effective user role (prioritize customer_role from API over system role)
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {string} - Effective user role
 */
export const getEffectiveUserRole = (user, customerRoles = null) => {
  // If we have customer role data, use that as the primary role
  if (customerRoles?.customer_role) {
    return customerRoles.customer_role;
  }
  // Fallback to system role from user object
  return user?.role || null;
};

/**
 * Check if user is a payroll admin (company_admin or PAYROLL_ADMIN)
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user is payroll admin
 */
export const isPayrollAdmin = (user, customerRoles = null) => {
  if (!user) return false;
  const effectiveRole = getEffectiveUserRole(user, customerRoles);
  return effectiveRole === 'company_admin' || 
         effectiveRole === 'PAYROLL_ADMIN' ||
         hasPayrollRole(user, 'PAYROLL_ADMIN') || 
         getCustomerRole(customerRoles) === 'PAYROLL_ADMIN';
};

/**
 * Check if user is HR Manager
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user is HR Manager
 */
export const isHRManager = (user, customerRoles = null) => {
  if (!user) return false;
  const effectiveRole = getEffectiveUserRole(user, customerRoles);
  return effectiveRole === 'HR_MANAGER' ||
         hasPayrollRole(user, 'HR_MANAGER') || 
         getCustomerRole(customerRoles) === 'HR_MANAGER';
};

/**
 * Check if user is Accountant
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user is Accountant
 */
export const isAccountant = (user, customerRoles = null) => {
  if (!user) return false;
  const effectiveRole = getEffectiveUserRole(user, customerRoles);
  return effectiveRole === 'ACCOUNTANT' ||
         hasPayrollRole(user, 'ACCOUNTANT') || 
         getCustomerRole(customerRoles) === 'ACCOUNTANT';
};

/**
 * Check if user is Payroll Viewer
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user is Payroll Viewer
 */
export const isPayrollViewer = (user, customerRoles = null) => {
  if (!user) return false;
  const effectiveRole = getEffectiveUserRole(user, customerRoles);
  return effectiveRole === 'PAYROLL_VIEWER' ||
         hasPayrollRole(user, 'PAYROLL_VIEWER') || 
         getCustomerRole(customerRoles) === 'PAYROLL_VIEWER';
};

/**
 * Check if user can access a specific payroll feature
 * @param {Object} user - User object
 * @param {string} feature - Feature to check access for
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user can access the feature
 */
export const canAccessPayrollFeature = (user, feature, customerRoles = null) => {
  if (!canAccessPayroll(user)) return false;
  
  switch (feature) {
    case 'wallet_management':
      return isPayrollAdmin(user, customerRoles) || isAccountant(user, customerRoles);
    
    case 'hr_approvals':
      return isPayrollAdmin(user, customerRoles) || isHRManager(user, customerRoles);
    
    case 'accountant_approvals':
      return isPayrollAdmin(user, customerRoles) || isAccountant(user, customerRoles);
    
    case 'batch_management':
      return isPayrollAdmin(user, customerRoles) || isHRManager(user, customerRoles) || isAccountant(user, customerRoles);
    
    case 'payroll_reports':
      return isPayrollAdmin(user, customerRoles) || isHRManager(user, customerRoles) || isAccountant(user, customerRoles) || isPayrollViewer(user, customerRoles);
    
    case 'funding_calculator':
      return isPayrollAdmin(user, customerRoles) || isAccountant(user, customerRoles);
    
    case 'payment_tracking':
      return isPayrollAdmin(user, customerRoles) || isHRManager(user, customerRoles) || isAccountant(user, customerRoles);
    
    case 'payroll_deductions':
      return isPayrollAdmin(user, customerRoles) || isHRManager(user, customerRoles) || isAccountant(user, customerRoles);
    
    case 'employee_bank_details':
      return isPayrollAdmin(user, customerRoles) || isHRManager(user, customerRoles) || isAccountant(user, customerRoles);
    
    case 'payment_flow_details':
      return isPayrollAdmin(user, customerRoles) || isHRManager(user, customerRoles) || isAccountant(user, customerRoles);
    
    default:
      return false;
  }
};

/**
 * Get user's display role (prioritize customer_role from API, then payroll roles, then system role)
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {string} - Display role
 */
export const getUserDisplayRole = (user, customerRoles = null) => {
  if (!user) return 'Unknown';
  
  // First priority: customer_role from API
  if (customerRoles?.customer_role) {
    return customerRoles.customer_role;
  }
  
  // Second priority: payroll roles
  if (user.payroll_roles && user.payroll_roles.length > 0) {
    return user.payroll_roles[0];
  }
  
  // Fallback: system role
  return user.role || 'Unknown';
};

/**
 * Check if user can manage employees
 * @param {Object} user - User object
 * @returns {boolean} - True if user can manage employees
 */
export const canManageEmployees = (user) => {
  if (!user) return false;
  
  // Company admins can manage all employees
  if (user.role === 'company_admin') return true;
  
  // Branch users can manage employees in their branch
  if (user.role === 'branch_user') return true;
  
  return false;
};

/**
 * Check if user can view cross-branch data
 * @param {Object} user - User object
 * @returns {boolean} - True if user can view cross-branch data
 */
export const canViewCrossBranchData = (user) => {
  if (!user) return false;
  return user.role === 'company_admin';
};

/**
 * Check if user can access admin features
 * @param {Object} user - User object
 * @returns {boolean} - True if user can access admin features
 */
export const canAccessAdminFeatures = (user) => {
  if (!user) return false;
  return user.role === 'company_admin';
};

/**
 * Get allowed batch statuses for a user based on their role
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {Array<string>|null} - Array of allowed statuses, or null for all statuses
 */
export const getAllowedBatchStatuses = (user, customerRoles = null) => {
  if (!user) return [];
  
  // Company admin and PAYROLL_ADMIN can see all statuses
  if (isPayrollAdmin(user, customerRoles)) {
    return null; // null means all statuses
  }
  
  // HR_MANAGER can only see PENDING_HR_APPROVAL
  if (isHRManager(user, customerRoles)) {
    return ['PENDING_HR_APPROVAL'];
  }
  
  // ACCOUNTANT can see specific statuses
  if (isAccountant(user, customerRoles)) {
    return ['PENDING_ACCOUNTANT_APPROVAL', 'APPROVED', 'EXECUTING', 'COMPLETED'];
  }
  
  // PAYROLL_VIEWER can only see COMPLETED
  if (isPayrollViewer(user, customerRoles)) {
    return ['COMPLETED'];
  }
  
  return [];
};

/**
 * Filter batches by allowed statuses for a user
 * @param {Array} batches - Array of batch objects
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {Array} - Filtered batches
 */
export const filterBatchesByRole = (batches, user, customerRoles = null) => {
  if (!batches || !Array.isArray(batches)) return [];
  
  const allowedStatuses = getAllowedBatchStatuses(user, customerRoles);
  
  // If null, return all batches (PAYROLL_ADMIN)
  if (allowedStatuses === null) {
    return batches;
  }
  
  // Filter batches by allowed statuses
  return batches.filter(batch => {
    const batchStatus = batch.status || batch.batch_status;
    return allowedStatuses.includes(batchStatus);
  });
};

/**
 * Check if user can create batches (has can_initiate_payroll permission)
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user can create batches
 */
export const canCreateBatch = (user, customerRoles = null) => {
  if (!user) return false;
  
  // Company admin can always create batches
  if (isPayrollAdmin(user, customerRoles)) return true;
  
  // Check permission
  return hasPayrollPermission(user, 'can_initiate_payroll');
};

/**
 * Check if user can approve HR batches (has can_approve_hr permission)
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user can approve HR batches
 */
export const canApproveHR = (user, customerRoles = null) => {
  if (!user) return false;
  
  // Company admin and PAYROLL_ADMIN can always approve
  if (isPayrollAdmin(user, customerRoles)) return true;
  
  // HR_MANAGER can approve
  if (isHRManager(user, customerRoles)) return true;
  
  // Check permission
  return hasPayrollPermission(user, 'can_approve_hr');
};

/**
 * Check if user can approve Accountant batches (has can_approve_accountant permission)
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user can approve Accountant batches
 */
export const canApproveAccountant = (user, customerRoles = null) => {
  if (!user) return false;
  
  // Company admin and PAYROLL_ADMIN can always approve
  if (isPayrollAdmin(user, customerRoles)) return true;
  
  // ACCOUNTANT can approve
  if (isAccountant(user, customerRoles)) return true;
  
  // Check permission
  return hasPayrollPermission(user, 'can_approve_accountant');
};

/**
 * Check if user can manage bank details (has can_create_bank_details permission)
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user can manage bank details
 */
export const canManageBankDetails = (user, customerRoles = null) => {
  if (!user) return false;
  
  // Company admin can always manage
  if (isPayrollAdmin(user, customerRoles)) return true;
  
  // Check permission
  return hasPayrollPermission(user, 'can_create_bank_details');
};

/**
 * Check if user can manage funding (has can_manage_funding permission)
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user can manage funding
 */
export const canManageFunding = (user, customerRoles = null) => {
  if (!user) return false;
  
  // Company admin can always manage
  if (isPayrollAdmin(user, customerRoles)) return true;
  
  // ACCOUNTANT can manage funding
  if (isAccountant(user, customerRoles)) return true;
  
  // Check permission
  return hasPayrollPermission(user, 'can_manage_funding');
};

/**
 * Check if user can view wallet (read-only access)
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user can view wallet
 */
export const canViewWallet = (user, customerRoles = null) => {
  if (!user) return false;
  
  // Company admin can always view
  if (isPayrollAdmin(user, customerRoles)) return true;
  
  // ACCOUNTANT can view and manage
  if (isAccountant(user, customerRoles)) return true;
  
  // HR_MANAGER can view (read-only)
  if (isHRManager(user, customerRoles)) return true;
  
  return false;
};

/**
 * Check if user can manage deductions (create/update/delete)
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user can manage deductions
 */
export const canManageDeductions = (user, customerRoles = null) => {
  if (!user) return false;
  
  // Company admin can always manage
  if (isPayrollAdmin(user, customerRoles)) return true;
  
  // ACCOUNTANT can manage deductions (full CRUD)
  if (isAccountant(user, customerRoles)) return true;
  
  // HR_MANAGER can only view (view-only)
  // Return false for HR_MANAGER
  
  // Check permission
  return hasPayrollPermission(user, 'can_create_bank_details'); // Using similar permission check
};

/**
 * Check if user can manage settlements (create/update tax account, pay settlements)
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user can manage settlements
 */
export const canManageSettlements = (user, customerRoles = null) => {
  if (!user) return false;
  
  // Company admin can always manage
  if (isPayrollAdmin(user, customerRoles)) return true;
  
  // ACCOUNTANT can manage settlements (full access)
  if (isAccountant(user, customerRoles)) return true;
  
  // HR_MANAGER can only view (view-only)
  // Return false for HR_MANAGER
  
  return false;
};

/**
 * Check if user can view settlements (read-only access)
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user can view settlements
 */
export const canViewSettlements = (user, customerRoles = null) => {
  if (!user) return false;
  
  // Company admin can always view
  if (isPayrollAdmin(user, customerRoles)) return true;
  
  // ACCOUNTANT can view and manage
  if (isAccountant(user, customerRoles)) return true;
  
  // HR_MANAGER can view (read-only)
  if (isHRManager(user, customerRoles)) return true;
  
  return false;
};

/**
 * Check if user can update payroll settings
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user can update settings
 */
export const canUpdatePayrollSettings = (user, customerRoles = null) => {
  if (!user) return false;
  
  // Only company admin can update settings
  if (user.role === 'company_admin') return true;
  
  // PAYROLL_ADMIN can update
  if (isPayrollAdmin(user, customerRoles)) return true;
  
  return false;
};

/**
 * Check if user can view payroll settings (read-only access)
 * @param {Object} user - User object
 * @param {Object} customerRoles - Customer roles data from endpoint
 * @returns {boolean} - True if user can view settings
 */
export const canViewPayrollSettings = (user, customerRoles = null) => {
  if (!user) return false;
  
  // Company admin can view and update
  if (user.role === 'company_admin') return true;
  
  // PAYROLL_ADMIN can view and update
  if (isPayrollAdmin(user, customerRoles)) return true;
  
  // HR_MANAGER can view (read-only)
  if (isHRManager(user, customerRoles)) return true;
  
  // ACCOUNTANT can view (read-only)
  if (isAccountant(user, customerRoles)) return true;
  
  return false;
};

/**
 * Get user's accessible features based on role and permissions
 * @param {Object} user - User object
 * @returns {Object} - Object with accessible features
 */
export const getUserAccessibleFeatures = (user) => {
  if (!user) return {};
  
  const isAdmin = user.role === 'company_admin';
  const isBranchUser = user.role === 'branch_user';
  const canAccessPayrollFeatures = canAccessPayroll(user);
  
  return {
    // System features
    dashboard: true,
    employees: true,
    configuration: true,
    recentEntries: true,
    leave: true,
    settings: true,
    
    // Admin features
    branchManagement: isAdmin,
    userManagement: isAdmin,
    overtimeManagement: isAdmin,
    
    // Reports
    attendanceReports: true,
    overtimeReports: true,
    productivityReports: !isBranchUser, // Branch users cannot access
    performanceReports: !isBranchUser, // Branch users cannot access
    
    // Payroll features (Main Branch only)
    payroll: canAccessPayrollFeatures,
    enhancedPayroll: canAccessPayrollFeatures,
    walletManagement: canAccessPayrollFeatures && (isPayrollAdmin(user) || isAccountant(user)),
    hrApprovals: canAccessPayrollFeatures && (isPayrollAdmin(user) || isHRManager(user)),
    accountantApprovals: canAccessPayrollFeatures && (isPayrollAdmin(user) || isAccountant(user)),
    batchManagement: canAccessPayrollFeatures && (isPayrollAdmin(user) || isHRManager(user) || isAccountant(user)),
    payrollReports: canAccessPayrollFeatures && (isPayrollAdmin(user) || isHRManager(user) || isAccountant(user) || isPayrollViewer(user)),
    
    // AI Features (Branch users cannot access)
    aiFeatures: !isBranchUser,
    advancedAnalytics: !isBranchUser,
  };
};

const accessControl = {
  canAccessPayroll,
  hasPayrollRole,
  hasPayrollPermission,
  isPayrollAdmin,
  isHRManager,
  isAccountant,
  isPayrollViewer,
  canAccessPayrollFeature,
  getUserDisplayRole,
  canManageEmployees,
  canViewCrossBranchData,
  canAccessAdminFeatures,
  getUserAccessibleFeatures,
  getAllowedBatchStatuses,
  filterBatchesByRole,
  canCreateBatch,
  canApproveHR,
  canApproveAccountant,
  canManageBankDetails,
  canManageFunding,
  canViewWallet,
  canManageDeductions,
  canManageSettlements,
  canViewSettlements,
  canUpdatePayrollSettings,
  canViewPayrollSettings,
};

export default accessControl;